// Client-alias matching helpers for the frontend.
// These intentionally duplicate the backend logic in functions/lib/dataforseo-ai.ts
// (same GENERIC set, same algorithm) rather than sharing a module, because the
// functions/ tree and the src/ tree compile under different tsconfig targets and
// cannot cross-import.

// Loose normalisation: lowercase, drop punctuation, collapse whitespace. Keeps CJK.
function norm(s: string): string {
  return (s || '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

const CJK_RE = /[㐀-鿿豈-﫿぀-ヿ]/;
// Global variant for stripping ALL CJK in englishCore(). Kept separate from
// CJK_RE for parity with the backend mirror in functions/lib/dataforseo-ai.ts.
const CJK_STRIP_RE = /[㐀-鿿豈-﫿぀-ヿ]/g;

// English "core": strip CJK chars then normalise.
function englishCore(name: string): string {
  const ascii = name.replace(CJK_STRIP_RE, ' ');
  return norm(ascii);
}

// Generic terms that are NOT brand identifiers (lowercased).
const GENERIC = new Set([
  // English
  'tcm','acupuncture','clinic','medical','center','centre','chinese','sdn','bhd',
  'best','traditional','medicine','health','wellness','the','and','of','for',
  'johor','bahru','jb','malaysia','taman','sentosa','professional',
  // CJK
  '新山','针灸','中医','中药','诊所','专业','医馆','理疗','按摩','推拿','门诊',
  '保健','养生','马来西亚','柔佛','中心','治疗','调理','医师','大马','针灸科',
]);

// CJK-safe: \b does NOT fire after Han chars in JS regex, so anchor on a
// following colon (ASCII/full-width) or end-of-string instead. Matches a bare
// label ("理由") and a label line ("理由： …" / "Reason: …").
const JUNK_LABEL_RE = /^\s*(理由|原因|推荐理由|简介|地址|备注|说明|reason|note|why|address|tel|phone)\s*([:：]|$)/i;

// Client-name tokens that, in THIS answer, name the category rather than the
// brand (mirror of categoryTokens() in the backend). A token is category when
// OTHER recommended businesses use it as a descriptor after their own name —
// "Specialist PAIN International", "Alleviate PAIN Clinic" — at least twice, or
// once when the token is also in the keyword. The client's own branches lead
// with the brand word, and shortened forms of the client's name aren't "others".
const LEAD_SKIP = new Set(['the', 'dr', 'by']);
function categoryTokens(core: string, rankedNames: string[], keyword?: string): Set<string> {
  const kw = new Set(norm(keyword ?? '').split(' ').filter(Boolean));
  const others = rankedNames
    .map(n => norm(n))
    .filter(n => n && !n.includes(core) && !core.includes(n))
    .map(n => n.split(' ').filter(t => !LEAD_SKIP.has(t)));
  const out = new Set<string>();
  for (const t of core.split(' ')) {
    if (!t || GENERIC.has(t)) continue;
    const uses = others.filter(toks => toks.indexOf(t) > 0).length;
    if (uses >= 2 || (uses >= 1 && kw.has(t))) out.add(t);
  }
  return out;
}

// Build distinctive needles (English + CJK) from the client's short name and
// optional GBP full display name.
function buildClientNeedles(shortName: string, displayName: string | null | undefined, category: Set<string>): { en: string[]; enDistinct: string[]; cjk: string[] } {
  const HAN_RE = /\p{Script=Han}+/gu;
  const en: string[] = [];
  const cjk: string[] = [];

  // English core — ONLY from the short name. The GBP display name is SEO-stuffed
  // ("...Best TCM Acupuncture..."); deriving an English needle from it would
  // wrongly match a competitor literally named with one of those stuffed phrases.
  // Short-name core + CJK runs already cover every real alias.
  // Guard: only use the English core if it has at least one token that is not
  // GENERIC and not a category token, otherwise a client literally named
  // "Acupuncture Clinic" (or "Pain Clinic") would match every competitor.
  // Generically-named clients fall back to CJK needles only.
  const coreShort = englishCore(shortName);
  const enDistinct = coreShort.split(' ').filter(t => t && !GENERIC.has(t) && !category.has(t));
  if (coreShort.length >= 4 && enDistinct.length > 0) en.push(coreShort);

  const addHanRuns = (src: string) => {
    const runs = src.match(HAN_RE) ?? [];
    for (const run of runs) {
      if (run.length >= 2 && !GENERIC.has(run) && !cjk.includes(run)) {
        cjk.push(run);
      }
    }
  };
  addHanRuns(shortName);
  if (displayName) addHanRuns(displayName);

  return { en, enDistinct, cjk };
}

/**
 * Returns true if `candidate` is an alias for the client identified by
 * `shortName` (and optionally by the GBP full `displayName`).
 *
 * `context` is the answer the candidate came from: its ranked names and the
 * keyword asked. Client-name words that other businesses in that answer also
 * use ("pain" in "Alleviate Pain Clinic") don't count as brand evidence.
 *
 * False-match guard: generic-only names like "TCM Clinic" or "针灸" never match.
 * Minimum overlap for CJK is 3 chars to avoid spurious 2-char matches.
 */
export function isClientAlias(
  candidate: string,
  shortName: string,
  displayName?: string | null,
  context: { rankedNames?: string[]; keyword?: string } = {},
): boolean {
  const cand = norm(candidate);
  const candCore = englishCore(candidate);
  if (!cand) return false;

  const category = categoryTokens(englishCore(shortName), context.rankedNames ?? [], context.keyword);
  const { en, enDistinct, cjk } = buildClientNeedles(shortName, displayName, category);
  const candTokens = cand.split(' ');

  for (const n of en) {
    if (cand.includes(n) || candCore.includes(n)) return true;
    // Reverse containment (AI shortened the name) only counts when the short
    // form still carries a distinctive token — a bare "Pain Clinic" does not.
    if (n.includes(cand) && candTokens.some(t => enDistinct.includes(t))) return true;
  }
  for (const n of cjk) {
    if ((cand.includes(n) && n.length >= 3) || (n.includes(cand) && cand.length >= 3)) return true;
  }
  return false;
}

/**
 * Returns true if `line` is junk and should be excluded from the competitor
 * list (reason sub-bullets, label prefixes, generic-only terms, etc.).
 *
 * Mirrors the isJunkName() helper in the backend parser.
 */
export function isJunkCompetitorLine(line: string): boolean {
  if (!line) return true;
  // Reason / label prefixes ("理由：", "Reason:", etc.)
  if (JUNK_LABEL_RE.test(line.trim())) return true;
  // Purely a single GENERIC term
  if (GENERIC.has(line.trim().toLowerCase())) return true;
  // Too long to be a business name
  if (line.length > 60) return true;
  // No letters at all (pure punctuation / numbers)
  if (!/\p{L}/u.test(line)) return true;
  return false;
}
